import context from "lib/stores/context";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { useSnapshot } from "valtio";
import RadixSelect from "components/common/RadixSelect";
import classNames from "classnames";
import dictMap from "lib/helpers/dictMap";
import { ReactComponent as IconLayers } from "components/icons/Layers.svg";
import ProductIcons from "components/icons/products";
import RadioControl from "components/common/RadioControl/RadioControl";
import mapData from "lib/stores/mapData";
import apiData from "lib/stores/apiData";
import styles from "styles/MobileLayersPanel.module.scss";
import commonStyles from "styles/common.module.scss";

const MobileLayersPanel = () => {
  const { showMobileLayersOverlay } = useSnapshot(context);
  const { activeCategory, productsCountry, selectedProduct } = useSnapshot(
    mapData,
  );
  const { countries, products } = useSnapshot(apiData);

  const renderButtons = () => {
    const country = countries[productsCountry];
    let imports = new Set();
    country.imports.forEach((imp) => {
      const product = {
        name: imp.product,
        value: imp.product.toLowerCase(),
      };
      imports.add(JSON.stringify(product));
    });
    imports = Array.from(imports).map((imp) => {
      const product = JSON.parse(imp);
      product.icon = getIconForProduct(product.value);
      return product;
    });

    imports = Array.from(imports).sort((a, b) => {
      let aTotal = 0;
      let bTotal = 0;
      country.imports.forEach((imp) => {
        if (imp.product === a.name) {
          aTotal += imp.value;
        }
        if (imp.product === b.name) {
          bTotal += imp.value;
        }
      });
      if (aTotal > bTotal) return -1;
      return 1;
    });

    return (
      <RadioControl
        label="Products"
        className={classNames(
          styles["toolbar-control-radio-container-mobile"],
          `product-radio-${activeCategory} no-drag`,
        )}
        value={selectedProduct}
        onChange={changeHandler}
        options={imports}
      />
    );
  };

  const changeHandler = (value: string) => {
    mapData.selectedProduct = value;
  };

  const getIconForProduct = (product) => {
    return (
      <div className={commonStyles["product-svg"]}>
        <img alt="" src={products.get(product).icon} />
      </div>
    );
  };

  const getCountryOptions = () => {
    return dictMap(countries, (country) => {
      if (!country?.imports?.length) return null;
      return {
        label: `${country.name} `,
        value: country.iso,
      };
    })
      .filter((c) => {
        if (c === null) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        return 1;
      });
  };

  const setSelectedOption = (value: string) => {
    mapData.productsCountry = value;
    mapData.selectedProduct = countries[value].imports[0].product.toLowerCase();
    context.activeCountry = value;
  };

  if (!showMobileLayersOverlay) return null;
  return (
    <div className={classNames("px-6 pb-8", styles["mobile-layers-overlay"])}>
      <MobileLayersPanelHeader
        onClick={() => (context.showMobileLayersOverlay = false)}
      />
      {/* <div className="flex items-start justify-start flex-col mb-8"> */}
      {/*   <h4 className="text-xs font-bold pb-2">YEAR</h4> */}
      {/*   <RadioGroup.Root */}
      {/*     className={classNames(commonStyles["radio-group"])} */}
      {/*     defaultValue="2023" */}
      {/*     value={year} */}
      {/*     orientation="horizontal" */}
      {/*     onValueChange={(year: "2018" | "2023") => { */}
      {/*       mapData.year = year; */}
      {/*     }} */}
      {/*   > */}
      {/*     <RadioGroup.Item value="2018" asChild aria-label="2018"> */}
      {/*       <button type="button">2018</button> */}
      {/*     </RadioGroup.Item> */}
      {/*     <RadioGroup.Item value="2023" asChild aria-label="2023"> */}
      {/*       <button type="button">2023</button> */}
      {/*     </RadioGroup.Item> */}
      {/*   </RadioGroup.Root> */}
      {/* </div> */}
      <div className="flex items-start justify-start flex-col mb-8">
        <h4 className="text-xs font-bold pb-2">DATA</h4>
        <RadioGroup.Root
          className={classNames(commonStyles["radio-group"])}
          defaultValue="2023"
          value={activeCategory}
          orientation="horizontal"
          onValueChange={(
            category: "prevalence" | "vulnerability" | "gov-response",
          ) => {
            mapData.activeCategory = category;
          }}
        >
          <RadioGroup.Item value="prevalence" asChild aria-label="Prevalence">
            <button type="button">Prevalence</button>
          </RadioGroup.Item>
          <RadioGroup.Item
            value="vulnerability"
            asChild
            aria-label="Vulnerability"
          >
            <button type="button">Vulnerability</button>
          </RadioGroup.Item>
          <RadioGroup.Item
            value="gov-response"
            asChild
            aria-label="Gov. Response"
          >
            <button type="button">Gov. Response</button>
          </RadioGroup.Item>
        </RadioGroup.Root>
      </div>

      <MobileSelectImportCountry
        setSelectedOption={setSelectedOption}
        productsCountry={productsCountry}
        getCountryOptions={getCountryOptions}
      />
      {productsCountry && (
        <>
          <div className="flex items-start justify-start flex-col pb-2 w-full">
            <h5 className="text-xs font-normal pb-2 pt-4 source">
              Choose a product:
            </h5>
            <div className="w-full flex flex-wrap items-start justify-start">
              {renderButtons()}
            </div>
          </div>
          <button
            type="button"
            className={classNames(
              commonStyles["gsi-button"],
              commonStyles["button-root-style"],
              styles["is-variant-explore"],
            )}
            onClick={() => (context.showMobileLayersOverlay = false)}
          >
            Apply Filters
          </button>
        </>
      )}
    </div>
  );
};

// Mobile Select Import Country
const MobileSelectImportCountry = (
  { setSelectedOption, productsCountry, getCountryOptions },
) => {
  const { countries } = useSnapshot(apiData);

  const getPlaceholder = () => {
    try {
      if (countries[productsCountry]) {
        // return `${countries[productsCountry].name} (${productsCountry})`;
        return `${countries[productsCountry].name}`;
      } else {
        return "Select Country";
      }
    } catch (error) {
      return "Select Country";
    }
  };

  return (
    <div className="flex items-start justify-start flex-col w-full">
      <h4 className="text-xs font-bold pb-2">G20 IMPORTS</h4>
      <h5 className="text-xs font-normal pb-2">G20 Country:</h5>
      <RadixSelect
        onValueChange={setSelectedOption}
        value={productsCountry}
        className="w-full flex-1"
        side="bottom"
        triggerClassName="w-full h-11"
        selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-64 lg:w-48 xl:w-64 w-[calc(100vw-4rem)]"
        selectContentWrapperClassName="w-full"
        placeholder={getPlaceholder()}
        groups={[
          {
            label: "G20 Countries",
            items: getCountryOptions(),
          },
        ]}
      />
    </div>
  );
};

//  Mobile Layers Panel Header
const MobileLayersPanelHeader = ({ onClick }) => {
  return (
    <div className={"flex items-center justify-between w-full pb-6"}>
      <div className="h-8 flex items-center justify-center space-x-2 border-0 bg-white xl:hidden">
        <div>
          <IconLayers />
        </div>
        <span className="text-center text-2xl font-extrabold rubik">
          LAYERS
        </span>
      </div>
      <button
        className="border-[1px] border-black font-bold text-sm py-1 px-4 py-2"
        onClick={onClick}
      >
        Close
      </button>
    </div>
  );
};

// Export
export default MobileLayersPanel;
